import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Button,
    Empty,
    Modal,
    Segmented,
    Select,
    Spin,
    Tooltip,
    message,
} from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Ban, History, Settings2, Trash2 } from 'lucide-react';
import Layout from '../../Layout';
import useAuthStore from '../../store/authStore';
import { mixGetV1 } from '../../services/mixin';
import {
    bulkSyncTransitions,
    deleteTransition,
    getAppointmentTransitions,
    removeTransitionFromTag,
    resetMasterTransitions,
    type AppointmentTransitionItem,
} from '../../api/AppointmentTransition';
import {
    DEFAULT_SCREEN_CONTEXT,
    DEPARTMENT_FALLBACK_NAMES,
    SCREEN_CONTEXTS,
    TRANSITION_DEPARTMENT_IDS,
    screenContextLabel,
} from '../../config/appointmentTransition';
import TransitionChip from './TransitionChip';
import TagPicker from './TagPicker';
import DiffDrawer from './DiffDrawer';
import FeatureSettingsDrawer from './FeatureSettingsDrawer';
import DeadEndNotice, { findDeadEnds } from './DeadEndNotice';
import {
    chipBackground,
    readableTagColor,
    isAppointmentTag,
    isRowDiff,
    tagShortName,
    type DiffEntry,
    type MatrixRow,
    type TagOption,
} from './types';
import useIsDark from './useIsDark';
import { BRAND_BUTTON, MUTED_TEXT_BUTTON, brandButton } from './brand';

/** Gom các bước chuyển từ API thành từng hàng theo trạng thái nguồn, giữ thứ tự xuất hiện. */
function groupIntoRows(items: AppointmentTransitionItem[]): MatrixRow[] {
    const rows: MatrixRow[] = [];
    const index = new Map<number, MatrixRow>();
    items.forEach((it) => {
        let row = index.get(it.from_tag_id);
        if (!row) {
            row = { fromTagId: it.from_tag_id, dests: [] };
            index.set(it.from_tag_id, row);
            rows.push(row);
        }
        row.dests.push({
            id: it.id,
            toTagId: it.to_tag_id,
            isAllowed: it.is_allowed === 1,
        });
    });
    return rows;
}

function computeDiff(base: MatrixRow[], cur: MatrixRow[]): DiffEntry[] {
    const toMap = (rows: MatrixRow[]) => {
        const m = new Map<string, boolean>();
        rows.forEach((r) => r.dests.forEach((d) => m.set(`${r.fromTagId}>${d.toTagId}`, d.isAllowed)));
        return m;
    };
    const b = toMap(base);
    const c = toMap(cur);
    const out: DiffEntry[] = [];

    c.forEach((allowed, k) => {
        const [fromTagId, toTagId] = k.split('>').map(Number);
        if (!b.has(k)) {
            out.push({ kind: 'add', fromTagId, toTagId });
        } else if (b.get(k) !== allowed) {
            out.push({ kind: allowed ? 'enable' : 'disable', fromTagId, toTagId });
        }
    });
    b.forEach((_allowed, k) => {
        if (!c.has(k)) {
            const [fromTagId, toTagId] = k.split('>').map(Number);
            out.push({ kind: 'remove', fromTagId, toTagId });
        }
    });

    // Hàng chưa có đích nào không sinh ra thay đổi cấp đích, nên phải ghi nhận
    // riêng ở cấp hàng — nếu bỏ qua thì nút Lưu vẫn khóa và cảnh báo rời phạm vi
    // không chạy, người dùng mất hàng vừa thêm mà không hay.
    cur.forEach((r) => {
        if (r.dests.length) return;
        if (!base.some((x) => x.fromTagId === r.fromTagId)) {
            out.push({ kind: 'add-row', fromTagId: r.fromTagId, toTagId: r.fromTagId });
        }
    });
    base.forEach((r) => {
        if (r.dests.length) return;
        if (!cur.some((x) => x.fromTagId === r.fromTagId)) {
            out.push({ kind: 'remove-row', fromTagId: r.fromTagId, toTagId: r.fromTagId });
        }
    });

    return out;
}

export default function AppointmentTransitionsPage() {
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canEdit = isSuperAdmin || hasPermission('sale_tags.edit');
    const isDark = useIsDark();

    const [departmentType, setDepartmentType] = useState<number>(TRANSITION_DEPARTMENT_IDS[0]);
    const [screenContext, setScreenContext] = useState<number>(DEFAULT_SCREEN_CONTEXT);

    const [departmentNames, setDepartmentNames] = useState<Record<number, string>>({});
    const [tags, setTags] = useState<TagOption[]>([]);
    const [rows, setRows] = useState<MatrixRow[]>([]);
    const [baseline, setBaseline] = useState<MatrixRow[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    /** Phạm vi người dùng muốn chuyển sang khi còn thay đổi chưa lưu */
    const [pendingScope, setPendingScope] = useState<{ dept: number; ctx: number } | null>(null);

    const tagsById = useMemo(() => {
        const map: Record<number, TagOption> = {};
        tags.forEach((t) => (map[t.id] = t));
        return map;
    }, [tags]);

    /** Chỉ thẻ thuộc luồng lịch hẹn mới được chọn vào ma trận, bỏ thẻ sale. */
    const selectableTags = useMemo(() => tags.filter(isAppointmentTag), [tags]);

    const diff = useMemo(() => computeDiff(baseline, rows), [baseline, rows]);
    const dirty = diff.length > 0;

    const deadEnds = useMemo(() => findDeadEnds(rows), [rows]);

    /** Cuộn tới hàng của một trạng thái và nháy viền để dễ nhận ra. */
    const focusRow = (tagId: number) => {
        const el = document.getElementById(`transition-row-${tagId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400');
        window.setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 1600);
    };

    const scopeLabel = `${departmentNames[departmentType] ?? DEPARTMENT_FALLBACK_NAMES[departmentType] ?? `Phòng ban #${departmentType}`} · ${screenContextLabel(screenContext)}`;

    // ---------------------------------------------------------------- dữ liệu

    useEffect(() => {
        let alive = true;
        (async () => {
            const names: Record<number, string> = {};
            for (let page = 1; page <= 5; page += 1) {
                const res = await mixGetV1('/api/v1/be/departments', { page });
                const items = Array.isArray(res?.data) ? res.data : res?.data?.items;
                if (!res?.status || !Array.isArray(items) || !items.length) break;
                items.forEach((d: { id: number; name: string }) => {
                    names[d.id] = d.name;
                });
                const total = res?.data?.total ?? items.length;
                if (Object.keys(names).length >= total) break;
            }
            if (alive) setDepartmentNames(names);
        })();
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            const collected: TagOption[] = [];
            for (let page = 1; page <= 10; page += 1) {
                const res = await mixGetV1('/api/v1/be/customer-tags', { page });
                const items = res?.data?.items;
                if (!res?.status || !Array.isArray(items) || !items.length) break;
                // Giữ cả thẻ sale để còn tra được tên nếu ma trận đang trỏ tới chúng;
                // việc loại khỏi danh sách chọn làm ở `selectableTags`.
                items.forEach((t: TagOption) =>
                    collected.push({
                        id: t.id,
                        name: t.name,
                        code: t.code,
                        color: t.color,
                        group: t.group,
                    })
                );
                if (collected.length >= (res?.data?.total ?? collected.length)) break;
            }
            if (alive) setTags(collected);
        })();
        return () => {
            alive = false;
        };
    }, []);

    const fetchMatrix = useCallback(async (dept: number, ctx: number) => {
        setLoading(true);
        try {
            const res = await getAppointmentTransitions({
                department_type: dept,
                screen_context: ctx,
            });
            if (!res.status || !Array.isArray(res.data)) {
                message.error(res.msg || 'Không tải được ma trận chuyển trạng thái');
                setRows([]);
                setBaseline([]);
                return;
            }
            const grouped = groupIntoRows(res.data);
            setRows(grouped);
            setBaseline(JSON.parse(JSON.stringify(grouped)));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMatrix(departmentType, screenContext);
    }, [departmentType, screenContext, fetchMatrix]);

    // Chặn đóng tab khi còn thay đổi chưa lưu
    const dirtyRef = useRef(dirty);
    dirtyRef.current = dirty;
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!dirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // ---------------------------------------------------------------- thao tác

    const requestScope = (dept: number, ctx: number) => {
        if (dept === departmentType && ctx === screenContext) return;
        if (dirty) {
            setPendingScope({ dept, ctx });
            return;
        }
        setDepartmentType(dept);
        setScreenContext(ctx);
    };

    const applyPendingScope = () => {
        if (!pendingScope) return;
        setDepartmentType(pendingScope.dept);
        setScreenContext(pendingScope.ctx);
        setPendingScope(null);
    };

    const toggleDest = (fromTagId: number, toTagId: number) => {
        setRows((prev) =>
            prev.map((r) =>
                r.fromTagId !== fromTagId
                    ? r
                    : {
                          ...r,
                          dests: r.dests.map((d) =>
                              d.toTagId === toTagId ? { ...d, isAllowed: !d.isAllowed } : d
                          ),
                      }
            )
        );
    };

    const removeDest = (fromTagId: number, toTagId: number) => {
        setRows((prev) =>
            prev.map((r) =>
                r.fromTagId !== fromTagId
                    ? r
                    : { ...r, dests: r.dests.filter((d) => d.toTagId !== toTagId) }
            )
        );
    };

    const addDests = (fromTagId: number, ids: number[]) => {
        setRows((prev) =>
            prev.map((r) =>
                r.fromTagId !== fromTagId
                    ? r
                    : {
                          ...r,
                          dests: [
                              ...r.dests,
                              ...ids
                                  .filter((id) => !r.dests.some((d) => d.toTagId === id))
                                  .map((id) => ({ toTagId: id, isAllowed: true })),
                          ],
                      }
            )
        );
    };

    const addRows = (ids: number[]) => {
        setRows((prev) => [
            ...prev,
            ...ids
                .filter((id) => !prev.some((r) => r.fromTagId === id))
                .map((id) => ({ fromTagId: id, dests: [] })),
        ]);
    };

    const confirmRemoveRow = (fromTagId: number) => {
        const row = rows.find((r) => r.fromTagId === fromTagId);
        const tag = tagsById[fromTagId];
        Modal.confirm({
            title: `Xóa trạng thái ${tag?.code || tag?.name || `#${fromTagId}`}?`,
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    Sẽ xóa <span className="font-semibold">{row?.dests.length ?? 0} đích</span> của trạng
                    thái này. Khách đang ở trạng thái đó sẽ không chuyển tiếp được cho tới khi bạn cấu hình
                    lại.
                </div>
            ),
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            centered: true,
            onOk: () => setRows((prev) => prev.filter((r) => r.fromTagId !== fromTagId)),
        });
    };

    const revert = () => {
        setRows(JSON.parse(JSON.stringify(baseline)));
    };

    const confirmRevert = () => {
        Modal.confirm({
            title: 'Hoàn tác tất cả?',
            icon: <ExclamationCircleOutlined />,
            content: `Bỏ toàn bộ ${diff.length} thay đổi và quay lại cấu hình đã lưu gần nhất.`,
            okText: 'Hoàn tác',
            okType: 'danger',
            cancelText: 'Giữ lại',
            centered: true,
            onOk: revert,
        });
    };

    /**
     * Lưu: xóa những hàng/đích đã bỏ trước (bulk-sync chỉ nhận danh sách cần giữ),
     * rồi đồng bộ toàn bộ phần còn lại trong một transaction.
     */
    const save = async (): Promise<boolean> => {
        if (!diff.length) return true;
        setSaving(true);
        try {
            const scope = { department_type: departmentType, screen_context: screenContext };

            const removedRows = baseline.filter(
                (b) => !rows.some((r) => r.fromTagId === b.fromTagId)
            );
            for (const r of removedRows) {
                const res = await removeTransitionFromTag({ ...scope, from_tag_id: r.fromTagId });
                if (!res.status) {
                    message.error(res.msg || 'Xóa hàng trạng thái thất bại');
                    return false;
                }
            }

            const removedDestIds: number[] = [];
            baseline.forEach((b) => {
                const cur = rows.find((r) => r.fromTagId === b.fromTagId);
                if (!cur) return;
                b.dests.forEach((d) => {
                    if (d.id && !cur.dests.some((x) => x.toTagId === d.toTagId)) {
                        removedDestIds.push(d.id);
                    }
                });
            });
            for (const id of removedDestIds) {
                const res = await deleteTransition(id);
                if (!res.status) {
                    message.error(res.msg || 'Xóa bước chuyển thất bại');
                    return false;
                }
            }

            const transitions = rows.flatMap((r) =>
                r.dests.map((d) => ({
                    from_tag_id: r.fromTagId,
                    to_tag_id: d.toTagId,
                    is_allowed: (d.isAllowed ? 1 : 0) as 0 | 1,
                }))
            );
            const res = await bulkSyncTransitions({ ...scope, transitions });
            if (!res.status) {
                message.error(res.msg || 'Lưu ma trận thất bại');
                return false;
            }

            const emptyRows = rows.filter((r) => !r.dests.length);
            message.success(`Đã lưu ${diff.length} thay đổi`);
            if (emptyRows.length) {
                message.warning(
                    `${emptyRows.length} trạng thái chưa có đích nào nên không được lưu: ` +
                        emptyRows
                            .map((r) => tagsById[r.fromTagId]?.code || `#${r.fromTagId}`)
                            .join(', ')
                );
            }
            await fetchMatrix(departmentType, screenContext);
            return true;
        } finally {
            setSaving(false);
        }
    };

    const confirmResetMaster = () => {
        Modal.confirm({
            title: 'Nạp lại luồng gốc của Admin?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    Toàn bộ cấu hình tùy chỉnh của <span className="font-semibold">{scopeLabel}</span> sẽ bị
                    xóa và thay bằng luồng chuẩn mặc định. Thao tác này áp dụng ngay trên máy chủ, không chờ
                    nút Lưu.
                </div>
            ),
            okText: 'Nạp luồng gốc',
            okType: 'danger',
            cancelText: 'Hủy',
            centered: true,
            onOk: async () => {
                const res = await resetMasterTransitions({
                    department_type: departmentType,
                    screen_context: screenContext,
                });
                if (!res.status) {
                    message.error(res.msg || 'Khôi phục luồng gốc thất bại');
                    return;
                }
                message.success(res.msg || 'Đã khôi phục luồng gốc');
                await fetchMatrix(departmentType, screenContext);
            },
        });
    };

    // ---------------------------------------------------------------- render

    const allowedCount = rows.reduce(
        (sum, r) => sum + r.dests.filter((d) => d.isAllowed).length,
        0
    );
    const totalDests = rows.reduce((sum, r) => sum + r.dests.length, 0);

    const rowPickerReasons = useMemo(() => {
        const reasons: Record<number, string> = {};
        rows.forEach((r) => (reasons[r.fromTagId] = 'đã có'));
        return reasons;
    }, [rows]);

    return (
        <Layout>
            <Helmet>
                <title>Ma trận chuyển trạng thái | Khothi</title>
            </Helmet>

            <div>
                <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Ma trận chuyển trạng thái lịch hẹn
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-[#a0a0a0]">
                        Quy định khách đang ở trạng thái nào thì nhân viên của phòng ban đó được phép chuyển
                        sang những trạng thái nào.
                    </p>
                </div>

                {/* Phạm vi áp dụng — luôn nhìn thấy khi cuộn */}
                <div className="sticky top-0 z-20 bg-white dark:bg-[#141414] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-sm px-4 py-2.5">
                    <div className="flex flex-wrap items-end gap-x-7 gap-y-3">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[#8a8a8a] mb-1">
                                <span className="text-[#3e91ab]">1.</span> Phòng ban
                            </div>
                            <Select
                                value={departmentType}
                                onChange={(v) => requestScope(v, screenContext)}
                                className="min-w-[230px]"
                                options={TRANSITION_DEPARTMENT_IDS.map((id) => ({
                                    value: id,
                                    label:
                                        departmentNames[id] ??
                                        DEPARTMENT_FALLBACK_NAMES[id] ??
                                        `Phòng ban #${id}`,
                                }))}
                            />
                        </div>
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-[#8a8a8a] mb-1">
                                <span className="text-[#3e91ab]">2.</span> Ngữ cảnh màn hình
                            </div>
                            <Segmented
                                value={screenContext}
                                onChange={(v) => requestScope(departmentType, v as number)}
                                options={SCREEN_CONTEXTS.map((c) => ({
                                    value: c.value,
                                    label: <span className="px-1">{c.label}</span>,
                                }))}
                            />
                        </div>
                    </div>
                </div>

                {/* Thanh công cụ */}
                <div className="flex flex-wrap items-center gap-3 py-4">
                    <div className="flex-1 min-w-[200px] text-xs text-gray-500 dark:text-[#a0a0a0]">
                        <span className="font-semibold text-gray-700 dark:text-gray-200">{rows.length}</span> trạng thái nguồn ·{' '}
                        <span className="font-semibold text-gray-700 dark:text-gray-200">{allowedCount}</span> đích đang bật ·{' '}
                        <span className="font-semibold text-gray-700 dark:text-gray-200">{totalDests - allowedCount}</span> đã
                        tắt
                    </div>

                    <DeadEndNotice
                        deadEnds={deadEnds}
                        tagsById={tagsById}
                        isDark={isDark}
                        canEdit={canEdit}
                        onAddRow={(tagId) => addRows([tagId])}
                        onFocusRow={focusRow}
                    />

                    <Tooltip title="Tự chuyển trạng thái S khi check-in lịch hẹn">
                        <Button icon={<Settings2 className="w-3.5 h-3.5" />} onClick={() => setSettingsOpen(true)}>
                            Cài đặt tự động
                        </Button>
                    </Tooltip>

                    {canEdit && (
                        <Tooltip title="Xóa cấu hình tùy chỉnh và nạp lại luồng chuẩn của Admin">
                            <Button icon={<History className="w-3.5 h-3.5" />} onClick={confirmResetMaster}>
                                Luồng gốc
                            </Button>
                        </Tooltip>
                    )}

                    {canEdit && (
                        <TagPicker
                            title="Thêm trạng thái nguồn"
                            subtitle="Mỗi trạng thái là một hàng trong ma trận"
                            tags={selectableTags}
                            disabledReasons={rowPickerReasons}
                            onApply={addRows}
                        >
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className={BRAND_BUTTON}
                            >
                                Thêm trạng thái nguồn
                            </Button>
                        </TagPicker>
                    )}
                </div>

                {/* Ma trận */}
                <Spin spinning={loading}>
                    {!rows.length && !loading ? (
                        <div className="bg-white dark:bg-[#141414] border border-dashed border-gray-300 dark:border-[#3a3a3a] rounded-lg py-12 px-5 text-center">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span className="text-sm text-gray-500 dark:text-[#a0a0a0]">
                                        {scopeLabel} chưa có cấu hình. Nhân viên phòng này sẽ không đổi được
                                        trạng thái nào ở màn hình đó.
                                    </span>
                                }
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2.5">
                            {rows.map((row) => {
                                const fromTag = tagsById[row.fromTagId];
                                const color = readableTagColor(fromTag?.color, isDark);
                                const destReasons: Record<number, string> = {
                                    [row.fromTagId]: 'chính nó',
                                };
                                row.dests.forEach((d) => (destReasons[d.toTagId] = 'đã có'));

                                return (
                                    <section
                                        key={row.fromTagId}
                                        id={`transition-row-${row.fromTagId}`}
                                        className="group/row grid grid-cols-1 md:grid-cols-[268px_minmax(0,1fr)] gap-3 md:gap-4
                                                   bg-white dark:bg-[#141414] border border-gray-200 dark:border-[#2a2a2a] rounded-lg px-4 py-3.5
                                                   transition-shadow scroll-mt-20"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            {canEdit && (
                                                <Tooltip title="Xóa cả hàng trạng thái này">
                                                    <button
                                                        type="button"
                                                        onClick={() => confirmRemoveRow(row.fromTagId)}
                                                        className="shrink-0 p-1.5 rounded-md text-gray-400 dark:text-[#6b6b6b]
                                                                   hover:!text-red-500 hover:bg-red-50 dark:hover:bg-[#3a1d1b] transition-colors"
                                                        aria-label="Xóa hàng trạng thái"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </Tooltip>
                                            )}

                                            <span
                                                className="inline-flex items-center gap-2 min-w-0 px-2.5 py-1 rounded-full text-[13px] font-semibold"
                                                title={fromTag?.name}
                                                style={{ backgroundColor: chipBackground(fromTag?.color, isDark), color }}
                                            >
                                                <span
                                                    className="w-1.5 h-1.5 rounded-full shrink-0"
                                                    style={{ backgroundColor: color }}
                                                />
                                                {fromTag?.code && <span className="shrink-0">{fromTag.code}</span>}
                                                <span className="font-normal opacity-90 truncate">
                                                    {tagShortName(fromTag, row.fromTagId)}
                                                </span>
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap items-center gap-2">
                                            {row.dests.map((d) => (
                                                <TransitionChip
                                                    key={d.toTagId}
                                                    tag={tagsById[d.toTagId]}
                                                    toTagId={d.toTagId}
                                                    allowed={d.isAllowed}
                                                    disabled={!canEdit}
                                                    isDark={isDark}
                                                    changed={diff.some(
                                                        (e) =>
                                                            !isRowDiff(e) &&
                                                            e.fromTagId === row.fromTagId &&
                                                            e.toTagId === d.toTagId
                                                    )}
                                                    onToggle={() => toggleDest(row.fromTagId, d.toTagId)}
                                                    onRemove={() => removeDest(row.fromTagId, d.toTagId)}
                                                />
                                            ))}

                                            {!row.dests.length && (
                                                <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-[#8a8a8a]">
                                                    <Ban className="w-3.5 h-3.5" />
                                                    Chưa có đích — khách vào trạng thái này sẽ bị kẹt
                                                </span>
                                            )}

                                            {canEdit && (
                                                <TagPicker
                                                    title={`Thêm đích cho ${fromTag?.code || fromTag?.name || `#${row.fromTagId}`}`}
                                                    subtitle="Chọn nhiều trạng thái cùng lúc"
                                                    tags={selectableTags}
                                                    disabledReasons={destReasons}
                                                    onApply={(ids) => addDests(row.fromTagId, ids)}
                                                >
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed
                                                                   border-gray-300 dark:border-[#3a3a3a] text-xs font-medium text-gray-500 dark:text-[#a0a0a0]
                                                                   hover:border-[#3e91ab] hover:!text-[#3e91ab] transition-colors"
                                                    >
                                                        <PlusOutlined /> Thêm đích
                                                    </button>
                                                </TagPicker>
                                            )}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </Spin>

                {/* Thanh lưu — dính đáy màn hình */}
                {canEdit && (
                    <div
                        className="sticky bottom-0 z-20 mt-4 bg-white dark:bg-[#141414] border border-gray-200 dark:border-[#2a2a2a]
                                   rounded-lg shadow-sm px-4 py-3"
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <div
                                className={`flex-1 min-w-[190px] flex items-center gap-2 text-[13px] ${
                                    dirty ? 'text-orange-500 font-semibold' : 'text-gray-400 dark:text-[#8a8a8a]'
                                }`}
                            >
                                <span
                                    className={`w-2 h-2 rounded-full ${
                                        dirty ? 'bg-orange-500' : 'bg-gray-300'
                                    }`}
                                />
                                {dirty ? `${diff.length} thay đổi chưa lưu` : 'Chưa có thay đổi'}
                            </div>

                            <Button type="text" disabled={!dirty} className={MUTED_TEXT_BUTTON} onClick={() => setDiffOpen(true)}>
                                Xem thay đổi
                            </Button>
                            <Button type="text" disabled={!dirty} className={MUTED_TEXT_BUTTON} onClick={confirmRevert}>
                                Hoàn tác tất cả
                            </Button>
                            <Button
                                type="primary"
                                loading={saving}
                                disabled={!dirty}
                                onClick={save}
                                className={brandButton(dirty)}
                            >
                                Lưu cấu hình
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <DiffDrawer
                open={diffOpen}
                entries={diff}
                tagsById={tagsById}
                scopeLabel={scopeLabel}
                saving={saving}
                onClose={() => setDiffOpen(false)}
                onSave={async () => {
                    setDiffOpen(false);
                    await save();
                }}
            />

            <FeatureSettingsDrawer
                open={settingsOpen}
                canEdit={canEdit}
                tagsById={tagsById}
                selectableTags={selectableTags}
                onClose={() => setSettingsOpen(false)}
            />

            {/* Rời phạm vi khi còn thay đổi chưa lưu */}
            <Modal
                open={!!pendingScope}
                centered
                title={`Còn ${diff.length} thay đổi chưa lưu`}
                onCancel={() => setPendingScope(null)}
                footer={[
                    <Button key="cancel" onClick={() => setPendingScope(null)}>
                        Hủy
                    </Button>,
                    <Button
                        key="discard"
                        danger
                        onClick={() => {
                            revert();
                            applyPendingScope();
                        }}
                    >
                        Bỏ thay đổi
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={saving}
                        className={BRAND_BUTTON}
                        onClick={async () => {
                            const ok = await save();
                            if (ok) applyPendingScope();
                        }}
                    >
                        Lưu rồi chuyển
                    </Button>,
                ]}
            >
                <p className="text-sm text-gray-500 dark:text-[#a0a0a0]">
                    Bạn đang rời khỏi phạm vi{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-200">{scopeLabel}</span>. Thay đổi chưa lưu sẽ
                    mất.
                </p>
            </Modal>
        </Layout>
    );
}
